// poster.js: "Save as poster". Composes a 1800 x 2400 print from the brand
// nebula (world.snapshot(w, h) -> canvas, if the world offers it; otherwise
// a frame grabbed from the live canvas), with the brand typeset, a
// catalogue-style designation derived from the name, and the studio line.
import { hashStr } from './util.js';

const W = 1800, H = 2400;

// A deterministic, clearly fictional "designation" for the brand star,
// derived from its name (it is labelled as generated on the poster).
export function designation(name) {
  const h = hashStr(name.trim().toLowerCase());
  const ra = (h % 86400);                       // seconds of right ascension
  const dec = ((h >>> 7) % 16200) / 180 - 45;   // -45 .. +45 deg
  const rh = Math.floor(ra / 3600), rm = Math.floor((ra % 3600) / 60), rs = ra % 60;
  const dd = Math.floor(Math.abs(dec)), dm = Math.floor((Math.abs(dec) - dd) * 60);
  const code = `SLX ${String((h >>> 3) % 10000).padStart(4, '0')}${'ABCDEFGHJK'[(h >>> 11) % 10]}`;
  return {
    code,
    ra: `${String(rh).padStart(2, '0')}h ${String(rm).padStart(2, '0')}m ${String(rs).padStart(2, '0')}s`,
    dec: `${dec >= 0 ? '+' : '−'}${String(dd).padStart(2, '0')}° ${String(dm).padStart(2, '0')}′`,
  };
}

function fitFont(x, text, weight, family, maxW, start, stretch) {
  let size = start;
  for (let i = 0; i < 40; i++) {
    x.font = `${weight} ${size}px ${family}`;
    if (stretch && 'fontStretch' in x) x.fontStretch = stretch; // setting font resets it
    if (x.measureText(text).width <= maxW) break;
    size = Math.floor(size * 0.94);
  }
  return size;
}

function spaced(x, text, cx, y, tracking) {
  // canvas letterSpacing is not everywhere yet: set glyphs one by one
  const chars = Array.from(text);
  const widths = chars.map((c) => x.measureText(c).width);
  const total = widths.reduce((a, b) => a + b, 0) + tracking * (chars.length - 1);
  let px = cx - total / 2;
  const align = x.textAlign;
  x.textAlign = 'left';
  chars.forEach((c, i) => { x.fillText(c, px, y); px += widths[i] + tracking; });
  x.textAlign = align;
}

export async function makePoster({ name, world, glCanvas, renderSnapshot }) {
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const x = c.getContext('2d');
  x.fillStyle = '#05060C';
  x.fillRect(0, 0, W, H);

  // 1. the image
  let img = null;
  if (world && typeof world.snapshot === 'function') {
    try { img = await Promise.resolve(world.snapshot(W, H)); } catch (e) { console.warn('[v5] snapshot failed, using the live frame', e); img = null; }
  }
  if (!img && renderSnapshot) { try { img = await renderSnapshot(); } catch (e) { img = null; } }
  if (!img && glCanvas) img = glCanvas;
  if (img && img.width && img.height) {
    // cover-fit, subject in the upper-middle
    const s = Math.max(W / img.width, H / img.height);
    const dw = img.width * s, dh = img.height * s;
    x.drawImage(img, (W - dw) / 2, (H - dh) * 0.4, dw, dh);
  }
  // 2. a quiet dark floor for the type
  const g = x.createLinearGradient(0, H * 0.52, 0, H);
  g.addColorStop(0, 'rgba(5,6,12,0)');
  g.addColorStop(0.45, 'rgba(5,6,12,0.72)');
  g.addColorStop(1, 'rgba(5,6,12,0.96)');
  x.fillStyle = g;
  x.fillRect(0, H * 0.52, W, H * 0.48);
  const top = x.createLinearGradient(0, 0, 0, 260);
  top.addColorStop(0, 'rgba(5,6,12,0.7)');
  top.addColorStop(1, 'rgba(5,6,12,0)');
  x.fillStyle = top;
  x.fillRect(0, 0, W, 260);

  const d = designation(name);
  const DISPLAY = '"Anybody", "Archivo", system-ui, sans-serif';
  const MONO = '"JetBrains Mono", ui-monospace, monospace';
  // the brand's light (LIGHT.md ch 4, Ring-Nebula default): teal core, violet shell, crimson knots. No yellow.
  const A1 = '#5FE3E0', A2 = '#C58BFF', A3 = '#FF3B6B';
  try { if (document.fonts && document.fonts.load) await Promise.race([document.fonts.load(`800 120px ${DISPLAY}`), new Promise((r) => setTimeout(r, 1200))]); } catch (e) { /* draw with what is loaded */ }
  x.textBaseline = 'alphabetic';
  x.textAlign = 'center';

  // top line
  x.fillStyle = 'rgba(239,237,232,0.62)';
  x.font = `500 26px ${MONO}`;
  spaced(x, 'A STAR FOR', W / 2, 150, 9);

  // the name
  x.fillStyle = '#EFEDE8';
  const title = name.toUpperCase();
  const size = fitFont(x, title, 800, DISPLAY, W * 0.84, 230, 'expanded');
  x.shadowColor = 'rgba(0,0,0,0.55)'; x.shadowBlur = 40;
  x.fillText(title, W / 2, H * 0.8);
  if ('fontStretch' in x) x.fontStretch = 'normal';
  x.shadowBlur = 0;

  // rule + designation
  const rg = x.createLinearGradient(W / 2 - 90, 0, W / 2 + 90, 0);
  rg.addColorStop(0, A1); rg.addColorStop(0.55, A2); rg.addColorStop(1, A3);
  x.strokeStyle = rg; x.lineWidth = 2;
  x.beginPath(); x.moveTo(W / 2 - 90, H * 0.8 + 70); x.lineTo(W / 2 + 90, H * 0.8 + 70); x.stroke();
  const dg = x.createLinearGradient(W * 0.18, 0, W * 0.82, 0);
  dg.addColorStop(0, A1); dg.addColorStop(1, A2);
  x.fillStyle = dg;
  x.font = `500 30px ${MONO}`;
  spaced(x, `${d.code}   ·   RA ${d.ra}   ·   DEC ${d.dec}`, W / 2, H * 0.8 + 140, 5);
  x.fillStyle = 'rgba(239,237,232,0.5)';
  x.font = `400 22px ${MONO}`;
  spaced(x, 'A NEBULA GENERATED FROM ITS NAME · NOT AN OFFICIAL STAR NAME', W / 2, H * 0.8 + 196, 5);

  // foot
  x.fillStyle = 'rgba(239,237,232,0.78)';
  x.font = `500 24px ${MONO}`;
  spaced(x, 'STARLEXMEDIA.COM', W / 2, H - 110, 10);
  x.fillStyle = A2;
  x.beginPath(); x.arc(W / 2, H - 170, 5, 0, Math.PI * 2); x.fill();
  return c;
}

export function download(canvas, filename) {
  return new Promise((resolve) => {
    const done = (url) => {
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => { if (url.startsWith('blob:')) URL.revokeObjectURL(url); }, 8000);
      resolve(url);
    };
    if (canvas.toBlob) canvas.toBlob((b) => done(b ? URL.createObjectURL(b) : canvas.toDataURL('image/png')), 'image/png');
    else done(canvas.toDataURL('image/png'));
  });
}
